<script lang="ts" setup>
import type { DrawerRootEmits, DrawerRootProps } from "vaul-vue"
import type { ComputedRef } from "vue"
import { provide, ref, watch } from "vue"
import { useForwardPropsEmits } from "reka-ui"
import { DrawerRoot } from "vaul-vue"
import { DRAWER_STACK_KEY, useDrawerStackEntry } from "~/composables/useDrawerStack"

const props = withDefaults(defineProps<DrawerRootProps>(), {
  shouldScaleBackground: true,
})

const emits = defineEmits<DrawerRootEmits>()

const forwarded = useForwardPropsEmits(props, emits) as ComputedRef<Record<string, unknown>>

// Whether this Drawer is open, for the app-wide Drawer stack (see useDrawerStack.ts). Covers both
// ways our Drawers are driven: controlled (`open` / `v-model:open`) and uncontrolled (a
// `DrawerTrigger`, which only surfaces as `update:open`).
const isOpen = ref(props.open ?? props.defaultOpen ?? false)
watch(() => props.open, (open) => {
  if (open !== undefined) isOpen.value = open
})

function handleOpenChange(open: boolean) {
  isOpen.value = open
}

provide(DRAWER_STACK_KEY, useDrawerStackEntry(isOpen))
</script>

<template>
  <!-- `@update:open` is merged with the forwarded one (Vue merges listeners), not a replacement. -->
  <DrawerRoot
    v-slot="slotProps"
    data-slot="drawer"
    v-bind="forwarded"
    @update:open="handleOpenChange"
  >
    <slot v-bind="slotProps" />
  </DrawerRoot>
</template>
